import { cn } from "@core-ui/utils/cn";
import {
	ProviderPickerItem,
	ProviderPickerLoading,
	ProviderPickerRow,
} from "@ext/articleProvider/components/ProviderPicker/ProviderPickerItem";
import t from "@ext/localization/locale/translate";
import {
	DropdownEmpty,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
} from "@ui-kit/Dropdown";
import { Icon } from "@ui-kit/Icon";
import { type ReactNode, useId, useMemo } from "react";
import { TOOLS_BRANCH, TOOLS_SCOPE } from "../../model/constants";
import type { ToolsReference, ToolsTreeNode, ToolsType } from "../../model/types";
import { tui } from "../locale";
import { useCanReload } from "../store/useCanReload";
import { useWorkspaceToolsActions } from "../store/useWorkspaceToolsActions";
import { useEnabledToolsState } from "../store/useWorkspaceToolsStore";
import { disabledReason } from "../tree/disabledReason";
import { filterToolsTree } from "../tree/filterToolsTree";
import { ToolExpander } from "../tree/ToolExpander";
import { useFailureMessage } from "../tree/ToolsUnavailable";
import { useToolsRows } from "../tree/useToolsRows";
import { type ToolsView, useToolsView } from "../tree/useToolsView";

type OnToolSelect = (ref: ToolsReference, title: string) => void;

export type WorkspaceToolsPickerProps = {
	type: ToolsType;
	query: string;
	catalogItems: ReactNode;
	catalogEmpty: boolean;
	catalogNotFound?: boolean;
	onSelect: OnToolSelect;
};

const PickerList = ({ tall = false, children }: { tall?: boolean; children: ReactNode }) => (
	<div className={cn("min-h-0 flex-1 overflow-y-auto", tall ? "max-h-72" : "max-h-44")} data-testid="picker-list">
		{children}
	</div>
);

export const WorkspaceToolsPicker = ({
	type,
	query,
	catalogItems,
	catalogEmpty,
	catalogNotFound = false,
	onSelect,
}: WorkspaceToolsPickerProps) => {
	const enabled = !!useEnabledToolsState();
	const view = useToolsView(type);
	const found = useMemo(() => (view.kind === "tree" ? filterToolsTree(view.nodes, query) : null), [view, query]);
	if (!enabled) return <PickerList>{catalogItems}</PickerList>;

	const workspaceEmpty = view.kind === "tree" && !view.nodes.length;
	if (catalogEmpty && workspaceEmpty)
		return (
			<PickerList>
				<DropdownMenuItem disabled>{tui(`none.${type}`)}</DropdownMenuItem>
			</PickerList>
		);
	if ((catalogEmpty || catalogNotFound) && found?.length === 0)
		return (
			<PickerList>
				<NothingFound />
			</PickerList>
		);

	return (
		<>
			<PickerList tall>
				<DropdownMenuGroup data-testid="picker-group-catalog">
					<GroupLabel>{tui("scope.catalog")}</GroupLabel>
					{catalogItems || <DropdownMenuItem disabled>{tui(`none.${type}`)}</DropdownMenuItem>}
				</DropdownMenuGroup>
				<WorkspaceGroup found={found} onSelect={onSelect} query={query} type={type} view={view} />
			</PickerList>
			{view.kind === "unavailable" && <ReloadItem />}
		</>
	);
};

// Sticky and opaque, painted as the glass menu looks over a plain page: a glass backdrop of its own would let
// the rows show through and tint it like a hovered row.
const GroupLabel = ({ children }: { children: ReactNode }) => (
	<DropdownMenuLabel className="sticky top-0 z-10 bg-secondary-bg bg-[linear-gradient(hsl(var(--alpha-60)),hsl(var(--alpha-60)))] text-muted">
		{children}
	</DropdownMenuLabel>
);

const NothingFound = () => <DropdownEmpty>{t("list.no-results-found")}</DropdownEmpty>;

type WorkspaceGroupProps = {
	type: ToolsType;
	view: ToolsView;
	query: string;
	found: ToolsTreeNode[] | null;
	onSelect: OnToolSelect;
};

const WorkspaceGroup = ({ type, view, query, found, onSelect }: WorkspaceGroupProps) => {
	return (
		<DropdownMenuGroup data-testid="picker-group-workspace">
			<GroupLabel>{tui("scope.workspace")}</GroupLabel>
			{view.kind === "loading" ? (
				<ProviderPickerLoading />
			) : view.kind === "unavailable" ? (
				<UnavailableItem reason={view.reason} />
			) : !view.nodes.length ? (
				<DropdownMenuItem disabled>{tui(`none.${type}`)}</DropdownMenuItem>
			) : found?.length ? (
				<ToolRows nodes={view.nodes} onSelect={onSelect} query={query} type={type} />
			) : (
				<NothingFound />
			)}
		</DropdownMenuGroup>
	);
};

const UnavailableItem = ({ reason }: { reason: "failed" | "absent" }) => {
	const message = useFailureMessage(reason);

	return (
		<DropdownMenuItem disabled>
			<div className="flex flex-col" data-testid="workspace-tools-unavailable">
				{tui(`unavailable.${reason}`)}
				{message && <span className="text-xs">{message}</span>}
			</div>
		</DropdownMenuItem>
	);
};

/** Sits under the list rather than in it: an error that fills the list would otherwise scroll its way out of reach. */
const ReloadItem = () => {
	const canReload = useCanReload();
	const { reload } = useWorkspaceToolsActions();
	if (!canReload) return null;

	return (
		<>
			<DropdownMenuSeparator />
			<DropdownMenuItem onSelect={() => void reload()}>
				<Icon icon="refresh-cw" />
				{tui("reload")}
			</DropdownMenuItem>
		</>
	);
};

type ToolRowsProps = { type: ToolsType; nodes: ToolsTreeNode[]; query: string; onSelect: OnToolSelect };

const ToolRows = ({ type, nodes, query, onSelect }: ToolRowsProps) => {
	const { rows, toggle, searching } = useToolsRows(type, nodes, query);
	const hasChevrons = !searching && rows.some((row) => row.expanded !== null);

	return (
		<>
			{rows.map((row) =>
				row.expanded === null ? (
					<ToolRow
						depth={row.depth}
						gutter={hasChevrons}
						item={row.node}
						key={row.key}
						onSelect={onSelect}
						reason={disabledReason(row.node, type)}
					/>
				) : searching ? (
					<PathRow depth={row.depth} item={row.node} key={row.key} />
				) : (
					<FolderRow
						depth={row.depth}
						expanded={row.expanded}
						item={row.node}
						key={row.key}
						onToggle={() => toggle(row)}
					/>
				),
			)}
		</>
	);
};

const nodeTestId = (item: ToolsTreeNode) => `workspace-tools-node-${item.id}`;

const PathRow = ({ item, depth }: { item: ToolsTreeNode; depth: number }) => (
	<div className="flex items-center px-2 py-1.5 text-sm text-muted">
		<ProviderPickerRow depth={depth} rowTestId={nodeTestId(item)} title={item.title || tui("untitled")} />
	</div>
);

type FolderRowProps = { item: ToolsTreeNode; depth: number; expanded: boolean; onToggle: () => void };

const FolderRow = ({ item, depth, expanded, onToggle }: FolderRowProps) => (
	<ProviderPickerItem
		aria-expanded={expanded}
		depth={depth}
		leading={<ToolExpander expanded={expanded} />}
		onSelect={(event) => {
			event.preventDefault();
			onToggle();
		}}
		rowTestId={nodeTestId(item)}
		textValue={item.title}
		title={item.title || tui("untitled")}
	/>
);

type ToolRowProps = {
	item: ToolsTreeNode;
	depth: number;
	gutter: boolean;
	reason: string | null;
	onSelect: OnToolSelect;
};

const ToolRow = ({ item, depth, gutter, reason, onSelect }: ToolRowProps) => {
	const reasonId = useId();

	// Inert rather than disabled: a disabled item drops out of keyboard navigation, and its reason with it. The
	// reason is a line under the title: a tooltip of its own would fight the title's.
	return (
		<ProviderPickerItem
			aria-describedby={reason ? reasonId : undefined}
			aria-disabled={reason ? true : undefined}
			className={cn(reason && "text-muted")}
			depth={depth}
			leading={gutter && <ToolExpander expanded={null} />}
			note={
				reason && (
					<span aria-hidden className="text-xs" id={reasonId}>
						{reason}
					</span>
				)
			}
			onSelect={(event) => {
				if (reason) event.preventDefault();
				else onSelect({ id: item.id, type: TOOLS_SCOPE, branch: TOOLS_BRANCH }, item.title);
			}}
			rowTestId={nodeTestId(item)}
			textValue={item.title}
			title={item.title || tui("untitled")}
		/>
	);
};
